<template>
  <div class="video-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar 
      :title="$t('video.title')" 
      @click-left="toService"
      fixed
      safe-area-inset-top
    >
    </van-nav-bar>

    <!-- 分类标签 -->
    <div class="category-tabs">
      <van-tabs 
        v-model="activeTab" 
        :swipe-threshold="4" 
        @change="onTabChange"
        sticky
        :offset-top="46"
      >
        <van-tab 
          v-for="(category, index) in videoCategories" 
          :key="index" 
          :title="category.name" 
          :name="index"
        />
      </van-tabs>
    </div>

    <!-- 视频列表 -->
    <div class="video-content">
      <van-pull-refresh 
        v-model="isRefreshing" 
        @refresh="onRefresh"
        :disabled="isLoading"
      >
        <!-- 骨架屏 -->
        <VideoSkeleton v-if="showSkeleton" />
        
        <!-- 视频网格 -->
        <div v-else class="video-grid">
          <div 
            v-for="(video, index) in videoList" 
            :key="`${video.id}_${index}`"
            class="video-card"
            @click="playVideo(video.id)"
          >
            <div class="video-cover-container">
              <!-- 使用普通 img 标签替代 CachedImage，添加错误处理 -->
              <img 
                :src="getSafeImageUrl(video.vod_pic)"
                :alt="video.vod_name"
                class="video-cover"
                @error="(e) => handleImageError(video, e)"
                @load="() => handleImageLoad(video)"
                loading="lazy"
              />
              <div class="video-overlay">
                <div class="video-play-icon">
                  <van-icon name="play" size="24" />
                </div>
              </div>
            </div>
            
            <div class="video-info">
              <h3 class="video-title" :title="video.vod_name">
                {{ video.vod_name }}
              </h3>
              <p class="video-count">
                {{ $t('video.nums') }}: {{ video.count || 0 }}
              </p>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <van-empty
          v-if="!showSkeleton && videoList.length === 0"
          :description="$t('common.noData')"
          class="empty-state"
        />

        <!-- 加载更多箭头容器 -->
        <div v-if="!isFinished && videoList.length > 0" class="load-more-arrow-container">
          <div class="load-more-arrow" @click="loadMore">
            <div class="arrow-icon" :class="{ loading: isLoading }">
              <van-icon name="arrow-down" size="20" />
            </div>
          </div>
        </div>

        <!-- 没有更多数据提示 -->
        <div v-if="isFinished && videoList.length > 0" class="no-more-container">
          <div class="no-more-text">
            {{ $t('common.nomore') }}
          </div>
          <div class="total-count">
            {{ $t('common.total') }}: {{ totalCount }} {{ $t('common.items') }}
          </div>
        </div>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';
import cacheManager from '../../common/cache';

// 懒加载骨架屏组件
const VideoSkeleton = () => import('./components/VideoSkeleton.vue');

export default {
  name: 'VideoPage',
  components: {
    VideoSkeleton
  },
  data() {
    return {
      activeTab: 0,
      isRefreshing: false,
      isLoading: false,
      isFinished: false,
      showSkeleton: true,
      
      videoCategories: [],
      videoList: [],
      currentPage: 1,
      totalCount: 0,
      pageSize: 10,
      // 添加图片错误处理状态
      imageErrors: new Set()
    };
  },
  computed: {
    hasMoreData() {
      return this.videoList.length < this.totalCount;
    }
  },
  methods: {
    /**
     * 安全的图片URL处理
     */
    getSafeImageUrl(url) {
      if (!url) return '/img/video-placeholder.jpg';
      
      // 如果图片已经出错，直接返回占位图
      if (this.imageErrors.has(url)) {
        return '/img/video-placeholder.jpg';
      }
      
      // 如果是相对路径，直接返回
      if (url.startsWith('/') || url.startsWith('./')) {
        return url;
      }
      
      // 如果是完整URL，尝试添加时间戳避免缓存问题
      try {
        const urlObj = new URL(url);
        // 添加时间戳参数，避免浏览器缓存导致的CORS问题
        const separator = urlObj.search ? '&' : '?';
        return `${url}${separator}t=${Date.now()}`;
      } catch {
        // 如果URL解析失败，返回原URL
        return url;
      }
    },

    /**
     * 图片加载错误处理
     */
    handleImageError(video, event) {
      console.warn('图片加载失败:', video.vod_pic);
      // 添加到错误集合
      this.imageErrors.add(video.vod_pic);
      
      // 替换为占位图
      event.target.src = '/img/video-placeholder.jpg';
      event.target.onerror = null; // 防止无限循环
      
      // 可选：重试机制（如果需要）
      // this.retryImageLoad(video, event);
    },

    /**
     * 图片重试加载
     */
    retryImageLoad(video, event) {
      const maxRetries = 2;
      let retryCount = 0;
      
      const retry = () => {
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`重试加载图片 ${video.vod_pic}, 第 ${retryCount} 次`);
          
          // 使用新的时间戳重新加载
          const newUrl = `${video.vod_pic}?t=${Date.now() + retryCount}`;
          event.target.src = newUrl;
        }
      };
      
      // 延迟重试
      setTimeout(retry, 1000 * retryCount);
    },

    /**
     * 图片加载成功
     */
    handleImageLoad(video) {
      console.log('图片加载成功:', video.vod_pic);
      // 可以从错误集合中移除（如果之前失败过）
      this.imageErrors.delete(video.vod_pic);
    },

    /**
     * 获取视频分类
     */
    async getVideoCategories() {
      const cacheKey = 'video_class_list';
      
      try {
        // 尝试从缓存获取
        const cachedData = cacheManager.get(cacheKey);
        if (cachedData) {
          console.log('使用缓存的视频分类列表');
          this.videoCategories = cachedData;
          return;
        }
        
        // API请求
        const response = await this.$http({
          method: 'get',
          url: 'video_class'
        });
        
        this.videoCategories = response.data;
        
        // 缓存数据
        cacheManager.set(cacheKey, response.data, 60 * 1000);
        
      } catch (error) {
        console.error('获取视频分类失败:', error);
        Toast(this.$t('common.loadFailed'));
      }
    },

    /**
     * 获取视频列表
     */
    async getVideoList(reset = false) {
      if (reset) {
        this.currentPage = 1;
        this.isFinished = false;
        this.videoList = [];
        this.imageErrors.clear(); // 重置图片错误状态
      }
      
      const cacheKey = `video_list_${this.activeTab}_${this.currentPage}`;
      
      try {
        // 显示加载状态
        if (reset) {
          this.showSkeleton = true;
        } else {
          this.isLoading = true;
        }
        
        // 尝试从缓存获取
        const cachedData = cacheManager.get(cacheKey);
        if (cachedData && !reset) {
          console.log(`使用缓存的视频列表: 分类${this.activeTab}, 第${this.currentPage}页`);
          this.processVideoData(cachedData, reset);
          return;
        }
        
        // API请求
        const response = await this.$http({
          method: 'get',
          data: {
            id: this.activeTab,
            page: this.currentPage,
            pageSize: this.pageSize
          },
          url: 'video_list'
        });
        
        this.processVideoData(response.data, reset);
        
        // 缓存数据
        cacheManager.set(cacheKey, response.data, 60 * 1000);
        
      } catch (error) {
        console.error('获取视频列表失败:', error);
        Toast(this.$t('common.loadFailed'));
        this.isLoading = false;
        this.isRefreshing = false;
        this.showSkeleton = false;
      }
    },

    /**
     * 处理视频数据
     */
    processVideoData(data, reset) {
      const newVideos = data.data || [];
      
      if (reset) {
        this.videoList = newVideos;
      } else {
        this.videoList = [...this.videoList, ...newVideos];
      }
      
      this.totalCount = data.count || 0;
      this.currentPage++;
      
      // 更新加载状态
      this.isLoading = false;
      this.isRefreshing = false;
      this.showSkeleton = false;
      
      // 检查是否已加载所有数据
      if (this.videoList.length >= this.totalCount || newVideos.length < this.pageSize) {
        this.isFinished = true;
      }
      
      console.log(`加载了 ${newVideos.length} 个视频，总共 ${this.videoList.length}/${this.totalCount}`);
    },

    /**
     * 标签切换事件
     */
    onTabChange() {
      this.videoList = [];
      this.currentPage = 1;
      this.totalCount = 0;
      this.imageErrors.clear(); // 清除图片错误状态
      this.getVideoList(true);
    },

    /**
     * 加载更多（点击箭头）
     */
    async loadMore() {
      if (this.hasMoreData && !this.isLoading) {
        await this.getVideoList();
      }
    },

    /**
     * 下拉刷新
     */
    onRefresh() {
      this.getVideoList(true);
      Toast(this.$t('common.refresh'));
    },

    /**
     * 播放视频 - 移除余额判断，直接跳转
     */
    playVideo(id) {
      if (!localStorage.getItem('token')) {
        this.$router.push('/Login');
        return;
      }
      
      // 直接跳转到播放页面，不检查余额
      this.$router.push(`/PlayVideo?id=${id}`);
    },

    /**
     * 客服服务
     */
    toService() {
      if (this.$store.getters.getBaseInfo.iskefu === 1) {
        this.$router.push('/ServiceOnline');
      } else {
        Toast(this.$t('common.noallow'));
      }
    }
  },
  async created() {
    // 并行加载分类和列表数据
    await Promise.all([
      this.getVideoCategories(),
      this.getVideoList(true)
    ]);
  }
};
</script>

<style lang="less" scoped>
@import "../../assets/css/basenew.css";

.video-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  position: relative;
}

/* 修复导航栏样式 - 确保始终在最顶层 */
.van-nav-bar {
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002; /* 确保导航栏在最上面 */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top);
    background: inherit;
  }
  
  :deep(.van-nav-bar__title) {
    color: white;
    font-weight: 600;
    font-size: 40px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  :deep(.van-icon) {
    color: white;
  }
}

/* 修复分类标签样式 - 确保在导航栏下方 */
.category-tabs {
  position: sticky;
  top: 46px; /* 导航栏高度 */
  left: 0;
  right: 0;
  z-index: 1001; /* 确保在导航栏下方，但在内容上方 */
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  
  /* 确保分类标签不会被其他元素覆盖 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  
  :deep(.van-tabs__wrap) {
    height: 50px;
    position: relative;
    z-index: 1001;
  }
  
  :deep(.van-tab) {
    color: #666;
    font-size: 30px;
    font-weight: 500;
    padding: 0 20px;
    transition: all 0.3s ease;
    
    &:hover {
      color: #667eea;
    }
  }
  
  :deep(.van-tab--active) {
    color: #667eea;
    font-weight: 600;
    font-size: 32px;
  }
  
  :deep(.van-tabs__line) {
    background: linear-gradient(90deg, #3498db, #8e44ad);
    height: 3px;
    border-radius: 2px;
    bottom: 20px;
    z-index: 1001;
  }
}

/* 修复内容区域 - 确保内容在标签下方 */
.video-content {
  margin-top: 0;
  padding: 96px 12px 30px; /* 顶部padding = 导航栏46px + 标签栏50px */
  min-height: calc(100vh - 96px);
  position: relative;
  z-index: 1; /* 确保内容在标签下方 */
}

/* 视频网格布局 */
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 8px 0;
}

.video-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  }
}

/* 视频封面容器 */
.video-cover-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: #f8f9fa;
}

.video-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  .video-card:hover & {
    transform: scale(1.05);
  }
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  .video-card:hover & {
    opacity: 1;
  }
}

.video-play-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: scale(0.8);
  transition: all 0.3s ease;
  
  .video-card:hover & {
    transform: scale(1);
  }
}

/* 视频信息 */
.video-info {
  padding: 12px;
  
  .video-title {
    font-size: 24px;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.4;
    margin: 0 0 8px 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 40px;
  }
  
  .video-count {
    font-size: 20px;
    color: #718096;
    margin: 0;
    font-weight: 500;
  }
}

/* 空状态 */
.empty-state {
  padding: 60px 20px;
  
  :deep(.van-empty__description) {
    color: #a0aec0;
    font-size: 14px;
  }
}

/* 加载更多箭头容器 */
.load-more-arrow-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  margin-top: 10px;
}

.load-more-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 16px 24px;
  border-radius: 25px;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  min-width: 60px;
  min-height: 60px;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.4);
  }
}

.arrow-icon {
  transition: transform 0.3s ease;
  
  &.loading {
    animation: rotate 1s linear infinite;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 没有更多数据提示 */
.no-more-container {
  text-align: center;
  padding: 30px 20px;
  color: #a0aec0;
}

.no-more-text {
  font-size: 14px;
  margin-bottom: 8px;
}

.total-count {
  font-size: 12px;
  opacity: 0.7;
}

/* 修复下拉刷新样式 */
:deep(.van-pull-refresh) {
  min-height: calc(100vh - 96px);
  height: auto !important;
  position: relative;
  z-index: 1;
}

:deep(.van-pull-refresh__track) {
  min-height: calc(100vh - 96px) !important;
  height: auto !important;
  position: relative;
}

/* 确保内容区域可以完全滚动 */
.video-content {
  margin-top: 0;
  padding: 96px 12px 30px;
  min-height: calc(100vh - 96px);
  position: relative;
  z-index: 1;
  overflow: visible;
}

/* 加载更多箭头容器 - 确保在底部有足够空间 */
.load-more-arrow-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0 60px;
  margin-top: 10px;
  position: relative;
}

.load-more-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  width: 60px;
  height: 60px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.4);
  }
}

.arrow-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  
  &.loading {
    animation: rotate 1s linear infinite;
  }
  
  :deep(.van-icon) {
    font-size: 24px;
  }
}

/* 响应式设计 */
@media (max-width: 375px) {
  .video-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .video-content {
    padding: 96px 10px 30px;
  }
  
  .category-tabs {
    :deep(.van-tab) {
      padding: 0 16px;
      font-size: 13px;
    }
  }
  
  .load-more-arrow {
    padding: 14px 20px;
    min-width: 140px;
  }
}

@media (min-width: 768px) {
  .video-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  .video-content {
    padding: 96px 16px 30px;
  }
}

@media (min-width: 1024px) {
  .video-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .video-page {
    background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  }
  
  .video-card {
    background: #2d3748;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .video-info .video-title {
    color: #e2e8f0;
  }
  
  .video-info .video-count {
    color: #a0aec0;
  }
}

/* 无障碍访问支持 */
@media (prefers-reduced-motion: reduce) {
  .video-card,
  .video-cover,
  .video-overlay,
  .video-play-icon,
  .load-more-arrow {
    transition: none;
  }
  
  .video-card:hover,
  .load-more-arrow:hover {
    transform: none;
  }
  
  .arrow-icon.loading {
    animation: none;
  }
}

/* 修复层级问题的关键CSS */
.video-page {
  isolation: isolate;
}

.van-nav-bar {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
}

.category-tabs {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
}

.video-content {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}
</style>